import cloneDeep from "lodash/cloneDeep";
import filter from "lodash/filter";
import find from "lodash/find";
import findLast from "lodash/findLast";
import get from "lodash/get";
import has from "lodash/has";
import {
  addWeeks,
  eachDayOfInterval,
  endOfDay,
  getISOWeek,
  isBefore,
  isDate,
  isMonday,
  max,
  subWeeks,
  previousMonday,
} from "date-fns";

const CLIENT_HOUR_LEVELS = [0, 1, 2];

const CLIENT_HOUR_TYPES = [
  "supportGeneralPerWeek",
  "support1to1PerWeek",
  "support2to1PerWeek",
  "support3to1PerWeek",
  "careGeneralPerWeek",
  "care1to1PerWeek",
  "care2to1PerWeek",
  "caret3to1PerWeek",
];

const CLIENT_HOUR_TYPES_AND_CATEGORIES = [
  {
    category: "client.hours.category.support",
    items: [
      "supportGeneralPerWeek",
      "support1to1PerWeek",
      "support2to1PerWeek",
      "support3to1PerWeek",
    ],
  },
  {
    category: "client.hours.category.care",
    items: [
      "careGeneralPerWeek",
      "care1to1PerWeek",
      "care2to1PerWeek",
      "caret3to1PerWeek",
    ],
  },
];

/**
 * Return support hours for a client
 * @deprecated
 *
 * @TODO this needs to understand escalation "level",
 * and also the different types of support hours (1to1) etc.
 * this is just a temporary fix so that we can deploy client hour entry UI
 *
 * @param {Object} client
 * @returns int
 */
function getSupportHours(client) {
  if (has(client, "hours") && client.hours.length > 0) {
    const latestHours = client.hours.pop().definition;
    return get(latestHours, "level0.supportGeneralPerWeek", 0);
  }

  if (has(client, "supportHours") && client.supportHours.length > 0) {
    const latestHours = client.supportHours.pop();
    return get(latestHours, "hoursPerWeek", 0);
  }

  return 0;
}

/**
 * iterate hours object to ensure the client has the full skeleton of all properties
 *
 * @param {Object} hours
 * @return {Object}
 */
function fillDefaultSupportHours(hours) {
  const filledHours = { ...hours };
  CLIENT_HOUR_LEVELS.forEach((level) => {
    if (!has(filledHours, `level${level}`)) {
      filledHours[`level${level}`] = {};
    }
    CLIENT_HOUR_TYPES.forEach((type) => {
      if (!has(filledHours, `level${level}.${type}`)) {
        filledHours[`level${level}`][type] = "";
      }
    });
    if (!has(filledHours, `level${level}.supervision`)) {
      filledHours[`level${level}`].supervision = [];
    }
  });
  return filledHours;
}

/**
 * Given a client object, work out what escalation level we are at for a given date.
 * Defaults to  { level: 'level0', date: undefined }
 *
 * @param {Object} client
 * @param {Date} date
 * @returns {Object}
 */
function getClientEscalationLevelForDate(client, date) {
  if (!has(client, "careEscalationHistory")) {
    return { level: "level0", date: undefined };
  }
  // order careEscalationHistory by effectiveFrom date
  const history = cloneDeep(client.careEscalationHistory);
  history.sort((a, b) => {
    if (isBefore(a.effectiveFrom, b.effectiveFrom)) {
      return -1;
    }
    /* v8 ignore else */
    if (isBefore(b.effectiveFrom, a.effectiveFrom)) {
      return 1;
    }
    /* v8 ignore next */
    return 0;
  });
  // pick the latest escalationLevel from the array where date is before the date given
  const escalationLevel = findLast(history, (h) =>
    isBefore(h.effectiveFrom, date),
  );
  /* v8 ignore next */
  if (escalationLevel === undefined) {
    return { level: "level0", date: undefined };
  }
  return {
    level: get(escalationLevel, "escalationLevel", "level0"),
    date: escalationLevel.effectiveFrom,
  };
}

/**
 * Retrieve the client hour definitions for a client at a given date,
 * taking into consideration escalation levels
 *
 * @param {Object} client
 * @param {Date} date
 * @returns {Object}
 */
function getClientHourDefinitionsForDate(client, date) {
  // if the client is archived, they don't get any hours after the archived date
  if (has(client, "archivedOn") && isBefore(client.archivedOn, date)) {
    return { date: client.archivedOn };
  }
  const level = getClientEscalationLevelForDate(client, date);
  // order hours by effectiveFrom date
  const clientHours = cloneDeep(get(client, "hours", []));
  clientHours.sort((a, b) => {
    if (isBefore(a.effectiveFrom, b.effectiveFrom)) {
      return -1;
    }
    /* v8 ignore else */
    if (isBefore(b.effectiveFrom, a.effectiveFrom)) {
      return 1;
    }
    /* v8 ignore next */
    return 0;
  });
  // pick the latest hours from the array where date is before the date given
  const hourDefinition = findLast(clientHours, (hours) =>
    isBefore(hours.effectiveFrom, date),
  );
  if (hourDefinition === undefined) {
    return null;
  }
  return {
    ...get(hourDefinition, `definition.${level.level}`, {}),

    date: isDate(level.date)
      ? max([hourDefinition.effectiveFrom, level.date])
      : hourDefinition.effectiveFrom,
  };
}

/**
 * Returns an object with the support hour components for a client,
 * which can be used with Object.values({this}).reduce((a, b) => a + b, 0)
 * to get the total support hours
 *
 * @param {Object} client
 * @param {Date} date
 */
function getClientSupportHours(client, date) {
  if (has(client, "archivedOn") && isBefore(client.archivedOn, date)) {
    return {};
  }
  // handle old-format hours
  if (get(client, "hours", []).length === 0) {
    if (has(client, "supportHours") && client.supportHours.length > 0) {
      const latestHours = client.supportHours.pop();
      return {
        hoursPerWeek: get(latestHours, "hoursPerWeek", 0),
      };
    }
    return {};
  }
  let dateToUse = new Date();
  /* v8 ignore else */
  if (isDate(date)) {
    dateToUse = date;
  }
  const level = getClientHourDefinitionsForDate(client, dateToUse);
  if (level == null) {
    return {};
  }
  const result = {};
  find(CLIENT_HOUR_TYPES_AND_CATEGORIES, {
    category: "client.hours.category.support",
  }).items.forEach((type) => {
    result[type] = get(level, type, 0);
  });
  return result;
}

/**
 * Filter the clients to find out which have supervision hours for the given rota period
 * Returns an array with a particular structure:
 *
 * [{
 *    client,
 *    rotaSupervisionHours: [
 *      {
 *        date, // date that these hours are effective from
 *        hours: [
 *          { startTime, endTime },
 *          ...
 *        ],
 *      },
 *       ...
 *    ]
 * }]
 *
 * @param {Array} clients
 * @param {Object} rota
 * @returns {Array}
 */
function filterSupervisionClientsForRotaPeriod(clients, rota) {
  let start = rota.startDate;
  let end = rota.endDate;

  // CC-1265 if looking at a master rota, we should show the supervision client's data
  // for the current fortnight period, as the master rota has 1970 dates
  if (rota.status === "master") {
    start = new Date();
    if (!isMonday(start)) {
      start = previousMonday(start);
    }
    // pick a fortnight - we go for the one that starts on an odd week number,
    // as the start of the year is week 1.
    if (getISOWeek(start) % 2 === 0) {
      start = subWeeks(start, 1);
    }
    end = addWeeks(start, 2);
  }

  const daysToCheck = eachDayOfInterval({
    start,
    end,
  });
  const supervisionClients = [];
  clients.forEach((client) => {
    if (get(client, "hours", []).length === 0) {
      return;
    }
    daysToCheck.forEach((date) => {
      const hours = getClientHourDefinitionsForDate(client, endOfDay(date));
      if (get(hours, "supervision", []).length > 0) {
        const supervisionHours = {
          hours: hours.supervision,
          date: hours.date,
        };
        if (rota.status === "master") {
          // CC-1265 hack for when we're showing master rota data instead of actual rota data
          //  - used by ClientSuperivsionHours.vue component
          supervisionHours.masterRotaDate = rota.startDate;
        }
        const existingSupervisionClient = find(
          supervisionClients,
          (c) => c.client.id === client.id,
        );
        if (existingSupervisionClient === undefined) {
          supervisionClients.push({
            client,
            rotaSupervisionHours: [supervisionHours],
          });
        } else if (
          find(
            existingSupervisionClient.rotaSupervisionHours,
            (h) => JSON.stringify(h) === JSON.stringify(supervisionHours),
          ) === undefined
        ) {
          existingSupervisionClient.rotaSupervisionHours.push(supervisionHours);
        }
      }
    });
  });
  return supervisionClients;
}

/**
 * Work out if a particular client is confusable within a list of clients
 * @param {Object} client
 * @param {Array} clients
 * @return {Boolean}
 */
function clientIsConfusable(client, clients) {
  // is clients an array?
  if (!Array.isArray(clients)) {
    return false;
  }
  if (clients.length === 0) {
    return false;
  }
  if (clients.length === 1 && clients[0].id === client.id) {
    return false;
  }
  // does the client have the same initials as another client?
  const clientInitials = get(client, "initials", "").toUpperCase();
  // remove the client we're checking from the list
  const otherClients = filter(clients, (c) => c.id !== client.id);
  const clientWithSameInitials = find(
    otherClients,
    (c) => get(c, "initials", "").toUpperCase() === clientInitials,
  );
  return clientWithSameInitials !== undefined;
}

export {
  CLIENT_HOUR_LEVELS,
  CLIENT_HOUR_TYPES,
  CLIENT_HOUR_TYPES_AND_CATEGORIES,
  getSupportHours,
  fillDefaultSupportHours,
  getClientHourDefinitionsForDate,
  getClientEscalationLevelForDate,
  getClientSupportHours,
  filterSupervisionClientsForRotaPeriod,
  clientIsConfusable,
};
